import React, { useEffect, useState, useContext, useRef } from 'react';
import { Helpers } from 'Helpers';
import { ICatalogPlus, CatalogAvailabilityFlag } from 'Interfaces';
import { PlusIcon, MinusIcon, LockIcon } from 'icons/';
import { ActionButton } from 'actionButton/ActionButton';
import { ContextualHelpContext } from 'contextualHelp/contextualHelpContext';
import { ContextualHelpStepsIndex } from 'contextualHelp/ContextualHelpSteps';
import parse from 'html-react-parser';
import { strings } from 'strings';
import './catalogListRow.scss';

type CatalogListRowProps = {
    catalog: ICatalogPlus,
    isHidden: boolean,
    tabIndex: number,
    baseUrl: string,
    availabilityFlag?: CatalogAvailabilityFlag,
    onCatalogVisibilityChanged(catalog: ICatalogPlus): void,
    onRequestCatalogAccess(catalog: ICatalogPlus): void
}

export function CatalogListRow(props: CatalogListRowProps) {
    const catalog: ICatalogPlus = props.catalog,
        { showContextualHelp } = useContext(ContextualHelpContext),
        targetElementRef = useRef<HTMLButtonElement | null>(null),
        [isHidden, setIsHidden] = useState<boolean>(props.isHidden),
        [isRestricted, setIsRestricted] = useState<boolean>(catalog.accessStatus === "RequestRequired"),
        [hasBeenRequested, setHasBeebnRequested] = useState<boolean>(catalog.accessStatus === "Requested"),
        [hasReturnError, setHasReturnError] = useState<boolean>(catalog.accessStatus === "Error"),
        [isPending, setIsPending] = useState<boolean>(catalog.accessStatus === 'Pending'),
        [isDenied, setIsDenied] = useState<boolean>(catalog.accessStatus === 'Denied'),
        [isInfoPanelOpen, setInfoPanelOpen] = useState<boolean>(false),
        thumbnail: JSX.Element = Helpers.buildImageContent(catalog.image?.uri || '', null, 'catalog-list-row__no-image', props.baseUrl, undefined, 'no-catalog-thumbnail.png'),
        lastActiveVersionDate: string = catalog?.catalogVersions?.[0]?.updatedDate ? new Date(catalog.catalogVersions[0].updatedDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : catalog.updatedDate ? new Date(catalog.updatedDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '',
        category: string = (catalog.classification?.mainCategories && catalog.classification.mainCategories[0]) || '',
        bigBoxClassName: string = catalog.accessStatus === "RequestRequired" ? 'big-box' : '',
        tooltipText: string = displayToolTip(isRestricted, isPending, isDenied, isHidden, hasBeenRequested, hasReturnError, strings),

        handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            event.preventDefault();
            openInfoPanel();

            if (!isRestricted && catalog.accessStatus !== 'Pending' && catalog.accessStatus !== 'Denied' && catalog.accessStatus !== 'Error' && catalog.accessStatus !== 'Requested') {
                props.onCatalogVisibilityChanged(catalog);
                setIsHidden(!isHidden);
            }
        },
        openInfoPanel = () => {
            if (isRestricted) {
                setInfoPanelOpen(!isInfoPanelOpen);
            }
            if (!isRestricted) {
                setInfoPanelOpen(true);
                setTimeout(() => {
                    setInfoPanelOpen(false);
                }, 3000);
            }
        },
        handleActionConfirm = () => {
            if (isRestricted) {
                //click for Request access
                props.onRequestCatalogAccess(catalog);

            };
            setInfoPanelOpen(false);
        },
        handleMouseButtonOver = () => {
            showContextualHelp((isRestricted || isDenied || isPending || hasBeenRequested || hasReturnError) ?
                ContextualHelpStepsIndex.CATALOG_PADLOCK : isHidden ?
                ContextualHelpStepsIndex.CATALOG_PLUS : ContextualHelpStepsIndex.CATALOG_MINUS,
                targetElementRef, props.tabIndex);
        };

    useEffect(() => {
        if (props.catalog.accessStatus === 'RequestRequired') {
            setIsRestricted(true);
            setHasBeebnRequested(false);
            setHasReturnError(false);
            setIsPending(false);
            setIsDenied(false);
        } else if (props.catalog.accessStatus === 'Requested') {
            setIsRestricted(false);
            setHasBeebnRequested(true);
            setHasReturnError(false);
            setIsPending(false);
            setIsDenied(false);
        } else if (props.catalog.accessStatus === 'Error') {
            setIsRestricted(false);
            setHasBeebnRequested(false);
            setHasReturnError(true);
            setIsPending(false);
            setIsDenied(false);
        } else if (props.catalog.accessStatus === 'Pending') {
            setIsRestricted(false);
            setHasBeebnRequested(false);
            setHasReturnError(false);
            setIsPending(true);
            setIsDenied(false);
        } else if (props.catalog.accessStatus === 'Denied') {
            setIsRestricted(false);
            setHasBeebnRequested(false);
            setHasReturnError(false);
            setIsPending(false);
            setIsDenied(true);
        }
    }, [props.catalog.accessStatus]);

    useEffect(() => {
        setIsHidden(props.isHidden);
    }, [props.isHidden]);

    return (
        <div id={catalog.code}
            className={`catalog-list-row__container ${props.availabilityFlag ? `catalog-list-row__container--${props.availabilityFlag}` : ''} ${props.isHidden ? '' : 'is-active'}`}
        >
            {props.availabilityFlag &&
                <div className={`catalog-list-row__flag catalog-list-row__flag--${props.availabilityFlag}`}>
                    {props.availabilityFlag === 'new' ? strings['catalog.flag.new'] : strings['catalog.flag.updated']}
                </div>
            }
            <div className='catalog-list-row__thumbnail'>{thumbnail}</div>
            <div className='catalog-list-row__cell catalog-list-row__manufacturer'>{catalog.companyDisplayName}</div>
            <div className='catalog-list-row__cell catalog-list-row__category'><span>{category}</span></div>
            <div className='catalog-list-row__cell catalog-list-row__description'>{catalog.description}</div>
            <div className='catalog-list-row__cell catalog-list-row__version'>{catalog?.catalogVersions?.[0]?.version || ''}</div>
            <div className='catalog-list-row__cell catalog-list-row__date'>{lastActiveVersionDate}</div>

            {isInfoPanelOpen &&
                <div className={`catalog-list-row__confirm ${bigBoxClassName}  `}>
                    <div className='catalog-list-row__confirm-text'>{parse(tooltipText)}</div>
                    {isRestricted &&
                        <div className='catalog-list-row__confirm-btn'>
                            <ActionButton
                                label={'Confirm'}
                                tabIndex={props.tabIndex}
                                onClick={handleActionConfirm}
                            />
                        </div>
                    }
                    <div id="catalog-list-row__confirm-pointer"></div>
                </div>
            }
            <button className={`catalog-list-row__cell catalog-list-row__add-button ${isRestricted || isHidden ? 'is-active' : hasReturnError ? 'is-error' : ''} ${(isRestricted || isDenied || isPending || hasBeenRequested || hasReturnError) ? 'step-catalog-padlock_'+ props.tabIndex : isHidden ? 'step-catalog-plus_'+ props.tabIndex : 'step-catalog-minus_'+ props.tabIndex}`}
                ref={targetElementRef}
                tabIndex={props.tabIndex || 0}
                data-tooltip-id='catalogs'
                aria-label={`${catalog.name}: ${isRestricted ? strings['catalog.action.requestCatalog'] : (isDenied || isPending || hasBeenRequested || hasReturnError) ? strings['catalog.action.view.status'] : isHidden ? strings['catalog.action.addCatalog'] : strings['catalog.action.removeCatalog']}`}
                data-tooltip-content={isRestricted ? strings['catalog.action.requestCatalog'] : (isDenied || isPending || hasBeenRequested || hasReturnError) ? strings['catalog.action.view.status'] : isHidden ? strings['catalog.action.addCatalog'] : strings['catalog.action.removeCatalog']}
                data-tooltip-delay-show={1500}
                data-tooltip-place='bottom'
                disabled={false}
                onClick={handleClick}
                onMouseEnter={handleMouseButtonOver}
            >
                {isRestricted || isPending || isDenied || hasBeenRequested || hasReturnError
                    ? <LockIcon width={16} height={16} />
                    : isHidden
                        ? <PlusIcon width={16} height={16} />
                        : <MinusIcon width={16} height={16} />
                }
            </button>
        </div>
    );
}

//========================================================================
function displayToolTip(isRestricted: boolean, isPending: boolean, isDenied: boolean, isHidden: boolean, hasBeenRequested: boolean, hasReturnError: boolean, strings: { [key: string]: string }): string {
    if (isRestricted) {
        return strings['catalog.action.request'];
    } else if (isPending) {
        return strings['catalog.action.pending'];
    } else if (isDenied) {
        return strings['catalog.action.denied'];
    } else if (isHidden) {
        return strings['catalog.action.hide'];
    } else if (hasBeenRequested) {
        return strings['catalog.action.pending'];
    } else if (hasReturnError) {
        return strings['catalog.action.error'];
    } else {
        return strings['catalog.action.add'];
    }
}
